// The account's time zone (GET shell/status timeZone, the business-hours zone on Admin › SLA and hours).
// Every date, calendar day and time label in the app is in this zone. ShellStatusProvider sets it when the
// status loads; until then it is Eastern, the zone of accounts that never chose one.
export const DEFAULT_TIME_ZONE = 'America/New_York';

let currentZone = DEFAULT_TIME_ZONE;
const formats = new Map();

export function isValidTimeZone(zone) {
  if (typeof zone !== 'string' || !zone.trim()) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone.trim() });
    return true;
  } catch (e) {
    return false;
  }
}

/** Sets the account zone; an unknown or empty id keeps Eastern. Returns the zone in use. */
export function setAccountTimeZone(zone) {
  currentZone = isValidTimeZone(zone) ? zone.trim() : DEFAULT_TIME_ZONE;
  return currentZone;
}

export const accountTimeZone = () => currentZone;

/** A cached Intl.DateTimeFormat for the options in `zone` (default: the account zone at call time). */
export function zonedFormat(locale, options, zone = currentZone) {
  const key = `${zone}|${locale}|${JSON.stringify(options)}`;
  let format = formats.get(key);
  if (!format) {
    format = new Intl.DateTimeFormat(locale, { ...options, timeZone: zone });
    formats.set(key, format);
  }
  return format;
}

/** Formats an instant in the account zone, e.g. formatInZone(at, { hour: 'numeric', minute: '2-digit' }). */
export const formatInZone = (value, options, locale = 'en-US', zone = currentZone) => zonedFormat(locale, options, zone).format(value instanceof Date ? value : new Date(value));

/** The calendar day ("2026-10-15") of an instant in the account zone. */
export const calendarDay = (value, zone = currentZone) => formatInZone(value, { year: 'numeric', month: '2-digit', day: '2-digit' }, 'en-CA', zone);

const zoneName = (zone, style, at) => {
  const part = zonedFormat('en-US', { timeZoneName: style }, zone)
    .formatToParts(at)
    .find((p) => p.type === 'timeZoneName');
  return part ? part.value : '';
};

const isUtc = (zone) => /^(Etc\/)?(UTC|UCT|GMT|Zulu|Universal)$/i.test(zone);

/** "Eastern Time", "Central Time", "Pacific Time": the generic name, the same in summer and winter. */
export function timeZoneLabel(zone = currentZone, at = new Date()) {
  if (isUtc(zone)) return 'UTC';
  return zoneName(zone, 'longGeneric', at) || zoneName(zone, 'long', at) || zone;
}

/** "ET", "CT", "PT" (falls back to the short daylight-specific name, e.g. "MST", or the long label). */
export function timeZoneAbbreviation(zone = currentZone, at = new Date()) {
  if (isUtc(zone)) return 'UTC';
  const generic = zoneName(zone, 'shortGeneric', at);
  if (generic && !/\s/.test(generic)) return generic;
  const short = zoneName(zone, 'short', at);
  return short && !/\s/.test(short) ? short : timeZoneLabel(zone, at);
}
